import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Inbox, Mail, MessageCircle, Phone, Linkedin, Sparkles, Check, Copy, Send,
  ArrowRight, Clock, AlertTriangle, RotateCcw, Loader2, Settings2,
} from 'lucide-react';
import api from '../lib/api';
import { UnmatchedReplyCard } from '../components/inbox/UnmatchedReplyCard';

const CHANNEL_ICONS = { email: Mail, whatsapp: MessageCircle, call: Phone, voice: Phone, linkedin: Linkedin };
const TONE_BADGE = {
  critical: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
  high: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  medium: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  low: 'bg-muted text-muted-foreground',
};
const SLA = {
  overdue:  { label: 'Overdue',   cls: 'bg-red-500/10 text-red-600 border-red-500/30', Icon: AlertTriangle },
  due_soon: { label: 'Due soon',  cls: 'bg-amber-500/10 text-amber-600 border-amber-500/30', Icon: Clock },
  on_track: { label: 'On track',  cls: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/30', Icon: Clock },
  handled:  { label: 'Handled',   cls: 'bg-muted text-muted-foreground border-border', Icon: Check },
};
const SEND_HINT = {
  whatsapp: 'Sends now (only works inside WhatsApp\u2019s 24h window).',
  email: 'Sends via your connected mailbox.',
  linkedin: 'Queued to the browser extension to deliver.',
};

function age(mins) {
  if (mins == null) return '';
  if (mins < 60) return `${mins}m waiting`;
  const h = Math.floor(mins / 60);
  if (h < 24) return `${h}h waiting`;
  return `${Math.floor(h / 24)}d waiting`;
}

function ReplyCard({ item, onHandled, onReopen, onDraft, onSend, drafting, sending }) {
  const navigate = useNavigate();
  const [text, setText] = useState(item.suggested_reply_draft || '');
  useEffect(() => { if (item.suggested_reply_draft) setText((t) => t || item.suggested_reply_draft); }, [item.suggested_reply_draft]);
  const Ch = CHANNEL_ICONS[item.channel] || Mail;
  const sla = SLA[item.sla_status] || SLA.on_track;

  return (
    <Card data-testid={`reply-card-${item.lead_id}`} className="overflow-hidden">
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-base leading-none" aria-hidden>{item.intent_emoji}</span>
              <p className="font-semibold truncate">{item.name}
                {item.company && <span className="text-muted-foreground font-normal"> · {item.company}</span>}
              </p>
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
              <span className="inline-flex items-center gap-1 capitalize"><Ch className="h-3.5 w-3.5" />{item.channel}</span>
              <span>·</span><span>{age(item.age_minutes)}</span>
            </div>
          </div>
          <div className="flex flex-col items-end gap-1.5 shrink-0">
            <Badge className={`text-[10px] ${TONE_BADGE[item.tone] || TONE_BADGE.low}`} data-testid={`reply-intent-${item.lead_id}`}>
              {item.intent_label}
            </Badge>
            <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium ${sla.cls}`} data-testid={`reply-sla-${item.lead_id}`}>
              <sla.Icon className="h-3 w-3" />{sla.label}
            </span>
          </div>
        </div>

        {item.message && (
          <p className="mt-3 rounded-lg bg-muted/50 p-3 text-sm leading-relaxed" data-testid={`reply-message-${item.lead_id}`}>
            “{item.message}”
          </p>
        )}

        {/* Reply composer — prefilled with the AI draft, editable, one-click send */}
        {!item.handled && item.can_send && (
          <div className="mt-3">
            <Textarea
              data-testid={`reply-composer-${item.lead_id}`}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={item.suggested_reply_draft ? '' : 'Write a reply, or tap “AI draft”…'}
              className="min-h-[72px] text-sm"
            />
            <div className="mt-1.5 flex items-center gap-2">
              <Button size="sm" className="gap-1.5" disabled={sending || !text.trim()}
                data-testid={`reply-send-${item.lead_id}`} onClick={() => onSend(item.lead_id, text, item.channel)}>
                {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                {item.channel === 'linkedin' ? 'Queue reply' : 'Send'}
              </Button>
              {!item.suggested_reply_draft && (
                <Button variant="outline" size="sm" className="gap-1.5" disabled={drafting}
                  data-testid={`reply-ai-draft-${item.lead_id}`} onClick={() => onDraft(item.lead_id)}>
                  {drafting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                  AI draft
                </Button>
              )}
              <Button variant="ghost" size="sm" className="gap-1.5" onClick={async () => {
                try { await navigator.clipboard.writeText(text); toast.success('Copied'); }
                catch { toast.error('Copy failed'); }
              }} data-testid={`reply-copy-${item.lead_id}`}>
                <Copy className="h-3.5 w-3.5" /> Copy
              </Button>
            </div>
            <p className="mt-1 text-[11px] text-muted-foreground">{SEND_HINT[item.channel]}</p>
          </div>
        )}

        <div className="mt-3 flex flex-wrap items-center gap-2">
          {item.handled ? (
            <Button variant="ghost" size="sm" className="gap-1.5" data-testid={`reply-reopen-${item.lead_id}`} onClick={() => onReopen(item.lead_id)}>
              <RotateCcw className="h-3.5 w-3.5" /> Reopen
            </Button>
          ) : (
            <Button variant="outline" size="sm" className="gap-1.5" data-testid={`reply-handled-${item.lead_id}`} onClick={() => onHandled(item.lead_id)}>
              <Check className="h-3.5 w-3.5" /> Mark handled
            </Button>
          )}
          <Button variant="ghost" size="sm" className="ml-auto gap-1.5 text-muted-foreground"
            data-testid={`reply-open-lead-${item.lead_id}`} onClick={() => navigate(`/leads/${item.lead_id}`)}>
            Open lead <ArrowRight className="h-3.5 w-3.5" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function SettingsDialog({ open, onOpenChange, settings, onSave }) {
  const [form, setForm] = useState(settings);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setForm(settings); }, [settings, open]);
  const save = async () => {
    setSaving(true);
    try { await onSave(form); toast.success('SLA settings saved'); onOpenChange(false); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not save'); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="sla-settings-dialog">
        <DialogHeader>
          <DialogTitle>Response SLA settings</DialogTitle>
          <DialogDescription>Set your reply timers and automation for this workspace.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="due" className="text-xs">Due-soon after (min)</Label>
              <Input id="due" type="number" min={5} value={form.due_soon_min}
                data-testid="sla-due-input"
                onChange={(e) => setForm({ ...form, due_soon_min: Number(e.target.value) })} />
            </div>
            <div>
              <Label htmlFor="over" className="text-xs">Overdue after (min)</Label>
              <Input id="over" type="number" min={10} value={form.overdue_min}
                data-testid="sla-overdue-input"
                onChange={(e) => setForm({ ...form, overdue_min: Number(e.target.value) })} />
            </div>
          </div>
          <div className="flex items-center justify-between rounded-lg border p-3">
            <div><p className="text-sm font-medium">Overdue nudge</p>
              <p className="text-xs text-muted-foreground">Alert me when a reply goes overdue.</p></div>
            <Switch checked={!!form.nudge_enabled} data-testid="sla-nudge-switch"
              onCheckedChange={(v) => setForm({ ...form, nudge_enabled: v })} />
          </div>
          <div className="flex items-center justify-between rounded-lg border p-3">
            <div><p className="text-sm font-medium">Auto-draft on arrival</p>
              <p className="text-xs text-muted-foreground">Write an AI reply the moment a lead replies (uses credits).</p></div>
            <Switch checked={!!form.auto_draft_enabled} data-testid="sla-autodraft-switch"
              onCheckedChange={(v) => setForm({ ...form, auto_draft_enabled: v })} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="sla-cancel-btn">Cancel</Button>
          <Button onClick={save} disabled={saving || !form.due_soon_min || !form.overdue_min || Number(form.overdue_min) <= Number(form.due_soon_min)} data-testid="sla-save-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function ResponseInbox() {
  const [tab, setTab] = useState('open');
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ open: 0, overdue: 0, unmatched: 0 });
  const [unmatched, setUnmatched] = useState([]);
  const [unmatchedBusy, setUnmatchedBusy] = useState(null);
  const [settings, setSettings] = useState({ due_soon_min: 120, overdue_min: 360, nudge_enabled: false, auto_draft_enabled: false });
  const [loading, setLoading] = useState(true);
  const [draftingId, setDraftingId] = useState(null);
  const [sendingId, setSendingId] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const load = useCallback((status) => {
    setLoading(true);
    if (status === 'unmatched') {
      Promise.all([api.get('/api/responses/unmatched?limit=100'), api.get('/api/responses/count')])
        .then(([u, c]) => { setUnmatched(u.data?.items || []); if (c.data?.counts) setCounts(c.data.counts); })
        .catch(() => toast.error('Could not load unmatched replies'))
        .finally(() => setLoading(false));
      return;
    }
    api.get(`/api/responses/inbox?status=${status}&limit=100`)
      .then((r) => {
        setItems(r.data?.items || []);
        setCounts(r.data?.counts || { open: 0, overdue: 0 });
        if (r.data?.sla) setSettings(r.data.sla);
      })
      .catch(() => toast.error('Could not load the inbox'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(tab); }, [tab, load]);

  const handleHandled = (id) => api.post(`/api/responses/${id}/handled`)
    .then(() => { toast.success('Marked handled'); load(tab); })
    .catch(() => toast.error('Action failed'));
  const handleReopen = (id) => api.post(`/api/responses/${id}/reopen`)
    .then(() => { toast.success('Reopened'); load(tab); })
    .catch(() => toast.error('Action failed'));
  const handleDraft = (id) => {
    setDraftingId(id);
    api.post(`/api/responses/${id}/draft`)
      .then((r) => { setItems((prev) => prev.map((it) => it.lead_id === id ? { ...it, suggested_reply_draft: r.data?.draft || '' } : it)); toast.success('AI draft ready'); })
      .catch((e) => toast.error(e?.response?.data?.detail || 'Draft failed'))
      .finally(() => setDraftingId(null));
  };
  const handleSend = (id, text, channel) => {
    setSendingId(id);
    api.post(`/api/responses/${id}/send`, { text })
      .then((r) => { toast.success(r.data?.status === 'queued' ? 'Reply queued' : 'Reply sent'); load(tab); })
      .catch((e) => toast.error(e?.response?.data?.detail || 'Send failed'))
      .finally(() => setSendingId(null));
  };
  const unmatchedAction = (path, id, body, okMsg) => {
    setUnmatchedBusy(id);
    return api.post(`/api/responses/unmatched/${id}/${path}`, body || {})
      .then((r) => { toast.success(r.data?.lead_name ? `${okMsg} — ${r.data.lead_name}` : okMsg); load('unmatched'); })
      .catch((e) => toast.error(e?.response?.data?.detail || 'Action failed'))
      .finally(() => setUnmatchedBusy(null));
  };
  const handleLinkUnmatched = (id, leadId) => unmatchedAction('link', id, { lead_id: leadId }, 'Reply linked');
  const handleCreateUnmatched = (id) => unmatchedAction('create-lead', id, null, 'Lead created & linked');
  const handleDismissUnmatched = (id) => unmatchedAction('dismiss', id, null, 'Dismissed');
  const saveSettings = (form) => api.put('/api/responses/settings', form)
    .then((r) => { if (r.data?.settings) setSettings(r.data.settings); load(tab); });

  return (
    <div className="mx-auto max-w-3xl px-4 py-6" data-testid="response-inbox-page">
      <div className="mb-5 flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <Inbox className="h-5 w-5" />
        </div>
        <div>
          <h1 className="text-xl font-bold leading-tight">Response Inbox</h1>
          <p className="text-sm text-muted-foreground">Every reply, one place — hottest and overdue first.</p>
        </div>
        <div className="ml-auto flex items-center gap-2 text-xs">
          <span className="rounded-full bg-muted px-2.5 py-1 font-medium" data-testid="inbox-count-open">{counts.open} open</span>
          {counts.overdue > 0 && (
            <span className="rounded-full bg-red-500/10 px-2.5 py-1 font-medium text-red-600" data-testid="inbox-count-overdue">{counts.overdue} overdue</span>
          )}
          <Button variant="ghost" size="icon" className="h-8 w-8" data-testid="inbox-settings-btn" onClick={() => setSettingsOpen(true)}>
            <Settings2 className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="mb-4">
        <TabsList data-testid="inbox-tabs">
          <TabsTrigger value="open" data-testid="inbox-tab-open">Open</TabsTrigger>
          <TabsTrigger value="overdue" data-testid="inbox-tab-overdue">Overdue</TabsTrigger>
          <TabsTrigger value="handled" data-testid="inbox-tab-handled">Handled</TabsTrigger>
          <TabsTrigger value="all" data-testid="inbox-tab-all">All</TabsTrigger>
          <TabsTrigger value="unmatched" data-testid="inbox-tab-unmatched">
            Unmatched{counts.unmatched > 0 ? <span className="ml-1.5 rounded-full bg-amber-500/15 px-1.5 text-[11px] font-semibold text-amber-700 dark:text-amber-300" data-testid="inbox-count-unmatched">{counts.unmatched}</span> : null}
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {loading ? (
        <div className="flex h-40 items-center justify-center" data-testid="inbox-loading">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      ) : tab === 'unmatched' ? (
        unmatched.length === 0 ? (
          <div className="flex h-48 flex-col items-center justify-center text-center text-muted-foreground" data-testid="inbox-unmatched-empty">
            <Inbox className="mb-3 h-10 w-10 opacity-40" />
            <p className="font-medium">No unmatched LinkedIn replies</p>
            <p className="text-sm">Replies the extension couldn&apos;t tie to a lead show up here so nobody is missed.</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="inbox-unmatched-list">
            {unmatched.map((u) => (
              <UnmatchedReplyCard key={u.id} item={u} busy={unmatchedBusy === u.id}
                onLink={handleLinkUnmatched} onCreate={handleCreateUnmatched} onDismiss={handleDismissUnmatched} />
            ))}
          </div>
        )
      ) : items.length === 0 ? (
        <div className="flex h-48 flex-col items-center justify-center text-center text-muted-foreground" data-testid="inbox-empty">
          <Inbox className="mb-3 h-10 w-10 opacity-40" />
          <p className="font-medium">No {tab === 'all' ? '' : tab} replies right now</p>
          <p className="text-sm">Inbound replies across LinkedIn, WhatsApp, email and voice land here.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="inbox-list">
          {items.map((it) => (
            <ReplyCard key={it.lead_id} item={it} drafting={draftingId === it.lead_id} sending={sendingId === it.lead_id}
              onHandled={handleHandled} onReopen={handleReopen} onDraft={handleDraft} onSend={handleSend} />
          ))}
        </div>
      )}

      <SettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} settings={settings} onSave={saveSettings} />
    </div>
  );
}
